import { getVal, setVal } from "oolib";
import { isTplAnnotationEnabled } from "@okf/ootils";
import { queryClient } from "@/index";

/**
 * Checks whether any dimension / data field is configured on the widget,
 * regardless of whether it produces content types for filters.
 * (e.g. annotation-based dimensions are valid dimensions but have no contentType)
 */
export const getWidgetHasDimensions = (widgetConfig, widgetType) => {
  if (!widgetConfig || !widgetConfig.props) return false;

  if (['WidgetBarChart', 'WidgetPieChart'].includes(widgetType)) {
    return !!getVal(widgetConfig, 'props.dimension1');
  }

  if (widgetType === 'WidgetHeatMapGrid') {
    return !!getVal(widgetConfig, 'props.dimension1') && !!getVal(widgetConfig, 'props.dimension2');
  }

  if (widgetType === 'WidgetLineChart') {
    return !!getVal(widgetConfig, 'props.dateField');
  }

  if (widgetType === 'WidgetMetricCard') {
    return !!getVal(widgetConfig, 'props.contentType');
  }

  if (widgetType === 'WidgetSankeyChart') {
    const mode = getVal(widgetConfig, 'props.mode') || 'dimensionalCrossover';
    if (mode === 'sequentialEventFlow') {
      const efc = getVal(widgetConfig, 'props.eventFlowConfig');
      return !!efc?.eventNameField?.valuePath && !!efc?.idField?.valuePath &&
        (!!efc?.orderField?.valuePath || !!efc?.timeField?.valuePath);
    }
    const dimensions = getVal(widgetConfig, 'props.dimensions') || [];
    const configuredDims = dimensions.filter((d) => d && (d.valuePath || d.rollupType));
    return configuredDims.length >= 2;
  }

  if (widgetType === 'WidgetAISummary' || widgetType === 'WidgetAICategorization') {
    return !!getVal(widgetConfig, 'props.contentType');
  }

  return false;
};

/**
 * Extracts content types from widget configuration based on widget type
 *
 * @param {Object} widgetConfig - The widget configuration object
 * @param {string} widgetType - The widget type (e.g., 'WidgetBarChart', 'WidgetLineChart', etc.)
 * @returns {Array<string>} - Array of unique content types
 */
export const getWidgetContentTypes = (widgetConfig, widgetType) => {
  if (!widgetConfig || !widgetConfig.props) {
    return [];
  }

  const contentTypes = new Set();

  // Lazily compute the set of contentTypes for tpls that have annotation enabled.
  // Used when a dimension is annotation-based (`isAnno: true`) — instead of a single
  // contentType, we want every annotation-enabled tpl to be available to the FilterModule.
  let annoContentTypesCache = null;
  const addAnnoContentTypes = () => {
    if (annoContentTypesCache === null) {
      const allTpls = queryClient.getQueryData("allTpls") || [];
      annoContentTypesCache = allTpls
        .filter(isTplAnnotationEnabled)
        .map((tpl) => tpl.kp_content_type)
        .filter(Boolean);
    }
    annoContentTypesCache.forEach((ct) => contentTypes.add(ct));
  };

  if (['WidgetBarChart', 'WidgetPieChart', 'WidgetHeatMapGrid'].includes(widgetType)) {
    const dimension1 = getVal(widgetConfig, 'props.dimension1');
    const dimension2 = getVal(widgetConfig, 'props.dimension2');

    if (dimension1?.isAnno) {
      addAnnoContentTypes();
    } else if (dimension1?.contentType) {
      contentTypes.add(dimension1.contentType);
    }
    if (dimension2?.isAnno) {
      addAnnoContentTypes();
    } else if (dimension2?.contentType) {
      contentTypes.add(dimension2.contentType);
    }

    if (dimension1?.rollupType === 'tagType' && dimension1?.rollupPath?.[0]) {
      contentTypes.add(dimension1.rollupPath[0]);
    }
    if (dimension1?.rollupType === 'valuePathType' && dimension1?.tagTypeCollectionToRollup) {
      contentTypes.add(dimension1.tagTypeCollectionToRollup);
    }
    if (dimension2?.rollupType === 'tagType' && dimension2?.rollupPath?.[0]) {
      contentTypes.add(dimension2.rollupPath[0]);
    }
    if (dimension2?.rollupType === 'valuePathType' && dimension2?.tagTypeCollectionToRollup) {
      contentTypes.add(dimension2.tagTypeCollectionToRollup);
    }
  }

  if (widgetType === 'WidgetSankeyChart') {
    // Dimensional crossover mode
    const dimensions = getVal(widgetConfig, 'props.dimensions') || [];
    dimensions.forEach((dim) => {
      if (dim?.isAnno) {
        addAnnoContentTypes();
      } else if (dim?.contentType) {
        contentTypes.add(dim.contentType);
      }
      if (dim?.rollupType === 'tagType' && dim?.rollupPath?.[0]) {
        contentTypes.add(dim.rollupPath[0]);
      }
      if (dim?.rollupType === 'valuePathType' && dim?.tagTypeCollectionToRollup) {
        contentTypes.add(dim.tagTypeCollectionToRollup);
      }
    });

    // Sequential event flow mode
    const eventFlowConfig = getVal(widgetConfig, 'props.eventFlowConfig');
    if (eventFlowConfig?.eventNameField?.contentType) {
      contentTypes.add(eventFlowConfig.eventNameField.contentType);
    }
  }

  if (widgetType === 'WidgetLineChart') {
    const dateField = getVal(widgetConfig, 'props.dateField');
    if (dateField?.contentType) {
      contentTypes.add(dateField.contentType);
    }
  }

  if (widgetType === 'WidgetMetricCard') {
    const field = getVal(widgetConfig, 'props.field');
    const contentType = getVal(widgetConfig, 'props.contentType');
    // An annotation-based field (Topics) stores contentType "annotations",
    // which is no tpl — so, as with annotation dimensions above, every
    // annotation-enabled tpl becomes available to the FilterModule instead.
    if (field?.isAnno) {
      addAnnoContentTypes();
    } else if (contentType) {
      contentTypes.add(contentType);
    }
  }

  if (widgetType === 'WidgetAISummary' || widgetType === 'WidgetAICategorization') {
    const contentType = getVal(widgetConfig, 'props.contentType');
    if (contentType) {
      contentTypes.add(contentType);
    }
  }

  return Array.from(contentTypes);
};

/**
 * After a dimension change, drop widget filters that belonged to a dataset the
 * widget no longer reads. A doc filter carries its dataset in
 * `source.contentType`; one that was in the previous content-type set but not
 * the new one is stale — it still goes to the backend and can silently empty
 * the widget while the Filters section shows only its clear button. Filters
 * without a content type (annotation filters) and ones from datasets the widget
 * never read directly (connected datasets) are left alone.
 *
 * @param {Object} widgetConfig  the config AFTER the dimension change
 * @param {string[]} prevContentTypes  getWidgetContentTypes() BEFORE the change
 * @param {string} widgetType
 */
export const pruneStaleWidgetFilters = (widgetConfig, prevContentTypes, widgetType) => {
  const filters = getVal(widgetConfig, 'props.widgetFilters');
  if (!Array.isArray(filters) || filters.length === 0) return widgetConfig;
  const prev = new Set(prevContentTypes);
  const next = new Set(getWidgetContentTypes(widgetConfig, widgetType));
  const kept = filters.filter((f) => {
    const ct = f?.source?.contentType;
    return !ct || next.has(ct) || !prev.has(ct);
  });
  return kept.length === filters.length ? widgetConfig : setVal(widgetConfig, 'props.widgetFilters', kept);
};
